import DOMPurify from 'dompurify';

/**
 * Sanitizes a string for safe HTML insertion by escaping HTML entities
 * Use this for text content that should NOT contain HTML
 */
export const escapeHtml = (text: string | null | undefined): string => {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

/**
 * Sanitizes HTML content, removing potentially dangerous elements
 * Use this when you want to allow some HTML formatting but remove scripts
 */
export const sanitizeHtml = (html: string | null | undefined): string => {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });
};

/**
 * Creates a safe text node - strips all HTML
 * Use this for user names, titles, and other plain text
 */
export const stripHtml = (text: string | null | undefined): string => {
  if (!text) return '';
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });
};

/**
 * Sanitizes full HTML documents for safe rendering in print windows/iframes
 * Allows structural elements while removing scripts and dangerous content
 * Use this for AI-generated HTML content that needs to retain formatting
 */
export const sanitizeFullHtml = (html: string | null | undefined): string => {
  if (!html) return '';
  
  // Preserve DOCTYPE declaration before sanitizing
  const hasDoctype = html.trim().toLowerCase().startsWith('<!doctype');
  const doctypeMatch = html.match(/^<!DOCTYPE[^>]*>/i);
  const doctype = doctypeMatch ? doctypeMatch[0] : '<!DOCTYPE html>';
  
  const sanitized = DOMPurify.sanitize(html, {
    WHOLE_DOCUMENT: true,
    ALLOWED_TAGS: [
      // Document structure
      'html', 'head', 'body', 'title', 'style', 'meta',
      // Content structure
      'div', 'span', 'p', 'br', 'hr',
      // Headers
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      // Lists
      'ul', 'ol', 'li',
      // Text formatting
      'strong', 'b', 'em', 'i', 'u', 's',
      // Tables
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
      // Links
      'a',
      // Images (but not from external sources)
      'img',
    ],
    ALLOWED_ATTR: [
      'class', 'id', 'style',
      'href', 'target', 'rel',
      'src', 'alt', 'width', 'height',
      'colspan', 'rowspan',
      'charset', 'name', 'content', // for meta tags
    ],
    // Prevent data URIs except for safe image types
    ALLOW_DATA_ATTR: false,
    // Don't allow external content
    ADD_TAGS: [],
    ADD_ATTR: [],
    // Remove potentially dangerous protocols
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  });
  
  // Add DOCTYPE back if original had it and sanitized output starts with <html
  if (hasDoctype && sanitized.trim().toLowerCase().startsWith('<html')) {
    return doctype + '\n' + sanitized;
  }
  
  return sanitized;
};
